import { Injectable } from '@angular/core';

import { BetslipConfig, BetslipOnboardingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { Store } from '@ngrx/store';
import { Observable, map, merge, withLatestFrom } from 'rxjs';

import { IBetslipRootState } from '../../base/store/state';
import { BetslipType } from '../../core/betslip-type';
import { betslipPicksListCountSelector } from '../picks/selectors';
import { betslipCurrentTypeSelector } from '../types/base/selectors';
import { betslipTypeStateSelector } from '../types/selectors';
import { IBetslipTypeState } from '../types/state';

export interface OnboardingMessage {
    onboardingImageUrl?: string;
    onboardingHeader: string;
    onboardingBody: string;
    onboardingFooter: string;
}

@Injectable()
export class BetslipOnboardingMessageService {
    onboardingMessage$: Observable<OnboardingMessage | null>;

    constructor(
        private sitecore: Sitecore,
        private betslipOnboardingConfig: BetslipOnboardingConfig,
        private betslipConfig: BetslipConfig,
        store: Store<IBetslipRootState>,
    ) {
        const updateOnboardingMessage$ = merge(
            store.select(betslipPicksListCountSelector).pipe(map(() => {})),
            store.select(betslipCurrentTypeSelector).pipe(map(() => {})),
        );
        this.onboardingMessage$ = updateOnboardingMessage$.pipe(
            withLatestFrom(store.select(betslipTypeStateSelector)),
            map(([_, betslipTypeState]) => this.mapOnboardingMessage(betslipTypeState.base.currentSelectedType, betslipTypeState)),
        );
    }

    mapOnboardingMessage(betslipType: BetslipType | null, betslipTypeState: IBetslipTypeState): OnboardingMessage | null {
        if (!this.isOnBoardingMessageRequired(betslipType, betslipTypeState)) {
            return null;
        }

        const message = this.mapBetTypeToOnboardingMessage(betslipType);
        if (this.validateOnboardingMessage(message)) {
            return message;
        }

        return null;
    }

    private isOnBoardingMessageRequired(betslipType: BetslipType | null, betslipTypeState: IBetslipTypeState): boolean {
        let isOnBoardingMessageRequired = false;
        switch (betslipType) {
            case BetslipType.Combo:
                isOnBoardingMessageRequired = Object.keys(betslipTypeState.comboBet.picks).length < this.betslipConfig.minComboBets;
                break;
            case BetslipType.Teaser:
                isOnBoardingMessageRequired = Object.keys(betslipTypeState.teaserBet.picks).length === 0;
                break;
            case BetslipType.System:
                isOnBoardingMessageRequired = Object.keys(betslipTypeState.comboBet.picks).length < this.betslipConfig.minSystemBets;
                break;
        }

        return isOnBoardingMessageRequired;
    }

    private mapBetTypeToOnboardingMessage(betslipType: BetslipType | null): OnboardingMessage | null {
        const messages = this.sitecore.betslip.onboarding.messages;

        switch (betslipType) {
            case BetslipType.Combo:
                return {
                    onboardingImageUrl: this.betslipOnboardingConfig.comboOnboardingImageUrl,
                    onboardingHeader: messages.ParlayOnboardingHeader,
                    onboardingBody: messages.ParlayOnboardingBody,
                    onboardingFooter: messages.ParlayOnboardingFooter,
                };
            case BetslipType.System:
                return {
                    onboardingImageUrl: this.betslipOnboardingConfig.systemOnboardingImageUrl,
                    onboardingHeader: messages.RoundRobinOnboardingHeader,
                    onboardingBody: messages.RoundRobinOnboardingBody,
                    onboardingFooter: messages.RoundRobinOnboardingFooter,
                };
            case BetslipType.Teaser:
                return {
                    onboardingImageUrl: this.betslipOnboardingConfig.teaserOnboardingImageUrl,
                    onboardingHeader: messages.TeaserOnboardingHeader,
                    onboardingBody: messages.TeaserOnboardingBody,
                    onboardingFooter: messages.TeaserOnboardingFooter,
                };
            default:
                return null;
        }
    }

    private validateOnboardingMessage(onboardingMessage: OnboardingMessage | null): boolean {
        return !!(
            onboardingMessage?.onboardingHeader &&
            onboardingMessage?.onboardingBody &&
            onboardingMessage?.onboardingFooter &&
            onboardingMessage?.onboardingImageUrl
        );
    }
}
